import { useCallback, useEffect, useState } from 'react'

import { Plus } from '@gravity-ui/icons'
import { Button, type SortDescriptor } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { UserIcon } from '@/components/icons'
import { ConfirmDeleteDialog } from '@/components/ui/ConfirmDeleteDialog'
import { EmptyState } from '@/components/ui/EmptyState'
import { PageHeader } from '@/components/ui/PageHeader'
import { useDriversStore } from '@/store/drivers.store'
import { useVehiclesStore } from '@/store/vehicles.store'
import { SortDirection } from '@/utils/sorting'

import { DriversTable } from './components/DriversTable'
import DriverDialog from './DriverDialog'

export default function DriversList() {
  const { t } = useTranslation()
  const { drivers, deleteDriver, isLoading, setPagination, pagination } = useDriversStore()
  const { vehicles } = useVehiclesStore()

  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'name',
    direction: 'ascending',
  })

  useEffect(() => {
    setPagination({
      sort: {
        key: sortDescriptor.column as string,
        direction: sortDescriptor.direction === 'ascending' ? SortDirection.ASC : SortDirection.DESC,
      },
    })
  }, [setPagination, sortDescriptor])

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | undefined>()
  const [deletingId, setDeletingId] = useState<string | undefined>()

  const handleAdd = useCallback(() => {
    setEditingId(undefined)
    setIsModalOpen(true)
  },[])

  const handleEdit = (id: string) => {
    setEditingId(id)
    setIsModalOpen(true)
  }

  const handleDeleteTrigger = (id: string) => {
    setDeletingId(id)
    setIsDeleteOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (deletingId) {
      await deleteDriver(deletingId)
      setIsDeleteOpen(false)
      setDeletingId(undefined)
    }
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingId(undefined)
  }

  return (
    <div className="p-5 space-y-5">
      <PageHeader
        title={t('drivers.title')}
        subtitle={`${pagination.total} ${t('drivers.subtitle')}`}
        actions={
          <Button
            onClick={handleAdd}
          >
            <Plus />
            {t('drivers.add_driver')}
          </Button>
        }
      />

      {drivers.length === 0 && !isLoading ? (
        <EmptyState
          icon={<UserIcon className={'size-8'}  />}
          title={t('drivers.empty_title', 'No drivers yet')}
          description={t('drivers.empty_subtitle', 'Add your first driver to start managing your fleet.')}
        >
          <Button
            size="lg"
            onClick={handleAdd}
          >
            <Plus />
            {t('drivers.empty_action', 'Add Driver')}
          </Button>
        </EmptyState>
      ) : (
        <DriversTable
          vehicles={vehicles}
          onEdit={handleEdit}
          onDelete={handleDeleteTrigger}
          sortDescriptor={sortDescriptor}
          onSortChange={setSortDescriptor}
        />
      )}

      <DriverDialog
        isOpen={isModalOpen}
        onOpenChange={open => !open && closeModal()}
        driverId={editingId}
      />

      <ConfirmDeleteDialog
        isOpen={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        onConfirm={handleDeleteConfirm}
        title={t('common.delete')}
        description={t('drivers.delete_confirm', 'Are you sure you want to delete this driver?')}
        isLoading={isLoading}
      />
    </div>
  )
}
